// Considered: ChannelCollaboratorsTabOrganism — rejected because it is channel-shaped (channel API, editor/contributor roles, dialog-based invite); the team section is inline cards with the 3-role model + Roles explainer per the V3 design. See spec Reuse Audit.
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import { usePublisherMembers } from '../../../hooks/usePublisherMembers';
import type { PublisherMember, PublisherPermissions } from 'types/publisher';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import AddTeammateMolecule from '../../molecules/marketplace/AddTeammateMolecule';
import './PublisherTeamSectionOrganism.scss';

// Explicit key lists (not template-built) so i18n tooling can find every key.
const ROLE_EXPLAINER_ROWS = [
  {
    labelKey: 'marketplace.publishers.manage.team.roles.ownerLabel',
    permKeys: [
      'marketplace.publishers.manage.team.roles.ownerPerm0',
      'marketplace.publishers.manage.team.roles.ownerPerm1',
      'marketplace.publishers.manage.team.roles.ownerPerm2',
      'marketplace.publishers.manage.team.roles.ownerPerm3',
      'marketplace.publishers.manage.team.roles.ownerPerm4',
    ],
  },
  {
    labelKey: 'marketplace.publishers.manage.team.roles.managerLabel',
    permKeys: [
      'marketplace.publishers.manage.team.roles.managerPerm0',
      'marketplace.publishers.manage.team.roles.managerPerm1',
      'marketplace.publishers.manage.team.roles.managerPerm2',
    ],
  },
  {
    labelKey: 'marketplace.publishers.manage.team.roles.creatorLabel',
    permKeys: [
      'marketplace.publishers.manage.team.roles.creatorPerm0',
      'marketplace.publishers.manage.team.roles.creatorPerm1',
    ],
  },
];

function formatJoinedDate(isoDate: string): string {
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime())
    ? isoDate
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function MemberAvatar({ name }: Readonly<{ name: string | null }>) {
  const initial = (name ?? '?').charAt(0).toUpperCase();
  return (
    <span className="publisher-team-section__avatar" aria-hidden="true">
      {initial}
    </span>
  );
}

function RoleExplainerSection() {
  const { t } = useTranslation();

  return (
    <div className="publisher-team-section__roles-list">
      {ROLE_EXPLAINER_ROWS.map(({ labelKey, permKeys }) => (
        <div key={labelKey} className="publisher-team-section__role-row">
          <span className="publisher-team-section__role-label">{t(labelKey)}</span>
          <ul className="publisher-team-section__role-perms">
            {permKeys.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export interface PublisherTeamSectionOrganismProps {
  publisherId: string;
  permissions: PublisherPermissions;
  onSelfRemoved?: () => void;
  className?: string;
}

const PublisherTeamSectionOrganism: React.FC<PublisherTeamSectionOrganismProps> = ({
  publisherId,
  permissions,
  onSelfRemoved,
  className = '',
}) => {
  const { t } = useTranslation();
  const { members, callerIdentityId, loading, loadFailed, isMutating, add, updateRole, remove } =
    usePublisherMembers(publisherId);

  const [removeTarget, setRemoveTarget] = useState<PublisherMember | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );

  const handleRemoveClick = useCallback((member: PublisherMember) => {
    setRemoveTarget(member);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeTarget) return;
    const targetId = removeTarget.identityId;
    const isSelf = targetId === callerIdentityId;
    setRemoveTarget(null);

    const success = await remove(targetId);
    if (success) {
      setFeedback({
        type: 'success',
        message: t('marketplace.publishers.manage.team.members.removeSuccess'),
      });
      if (isSelf && onSelfRemoved) onSelfRemoved();
    } else {
      setFeedback({
        type: 'error',
        message: t('marketplace.publishers.manage.team.members.removeError'),
      });
    }
  }, [removeTarget, callerIdentityId, remove, t, onSelfRemoved]);

  const handleRoleChange = useCallback(
    async (member: PublisherMember, newRole: 'manager' | 'creator') => {
      if (newRole === member.role) return;
      setFeedback(null);
      const success = await updateRole(member.identityId, newRole);
      if (success) {
        setFeedback({
          type: 'success',
          message: t('marketplace.publishers.manage.team.members.roleUpdateSuccess'),
        });
      } else {
        setFeedback({
          type: 'error',
          message: t('marketplace.publishers.manage.team.members.roleUpdateError'),
        });
      }
    },
    [updateRole, t]
  );

  const handleAdd = useCallback(
    async (identityId: string, role: 'manager' | 'creator'): Promise<boolean> => {
      setFeedback(null);
      const success = await add(identityId, role);
      if (!success) {
        setFeedback({
          type: 'error',
          message: t('marketplace.publishers.manage.team.members.addError'),
        });
      }
      return success;
    },
    [add, t]
  );

  const roleLabel = useCallback(
    (role: PublisherMember['role']) =>
      role === 'manager'
        ? t('marketplace.publishers.manage.team.members.roleManager')
        : t('marketplace.publishers.manage.team.members.roleCreator'),
    [t]
  );

  const classes = ['publisher-team-section', className].filter(Boolean).join(' ');

  if (loading && members.length === 0) {
    return (
      <div className={classes}>
        <span className="marketplace-marketplace__spinner" />
      </div>
    );
  }

  return (
    <div className={classes}>
      {/* Remove confirm dialog */}
      <ConfirmDialogAtom
        visible={!!removeTarget}
        message={t('marketplace.publishers.manage.team.members.removeConfirm', {
          name: removeTarget?.displayName ?? removeTarget?.identityId ?? '',
        })}
        acceptLabel={t('marketplace.publishers.manage.team.members.removeConfirmButton')}
        rejectLabel={t('marketplace.publishers.manage.team.members.cancelButton')}
        accept={handleRemoveConfirm}
        reject={() => setRemoveTarget(null)}
        onHide={() => setRemoveTarget(null)}
        variant="danger"
      />

      {/* Feedback */}
      {feedback && (
        <div
          className={`publisher-team-section__feedback publisher-team-section__feedback--${feedback.type}`}
          role="alert"
        >
          {feedback.message}
        </div>
      )}

      {/* Load error */}
      {loadFailed && (
        <div className="publisher-team-section__feedback publisher-team-section__feedback--error" role="alert">
          {t('marketplace.publishers.manage.team.members.loadError')}
        </div>
      )}

      {/* Members card */}
      <section className="publisher-team-section__card">
        <header className="publisher-team-section__card-header">
          <h2 className="publisher-team-section__card-title">
            {t('marketplace.publishers.manage.team.members.title')}
          </h2>
          <p className="publisher-team-section__card-subtitle">
            {t('marketplace.publishers.manage.team.members.subtitle')}
          </p>
        </header>
        <div className="publisher-team-section__card-body">
          {members.length === 0 ? (
            <p className="publisher-team-section__empty">
              {t('marketplace.publishers.manage.team.members.empty')}
            </p>
          ) : (
            <ul className="publisher-team-section__member-list">
              {members.map((member) => {
                const isOwner = member.role === 'owner';
                const isSelf = member.identityId === callerIdentityId;
                const displayName = member.displayName ?? member.identityId;
                const joinedStr = t('marketplace.publishers.manage.team.members.joinedDate', {
                  date: formatJoinedDate(member.joinedAt),
                });
                // Managers can only be role-changed/removed by someone with
                // transfer_ownership (server-enforced); mirror that in the UI.
                // Self-removal is the one carve-out the server allows.
                const canEditRole =
                  permissions.canManageGrants && (member.role !== 'manager' || permissions.canTransfer);
                const canRemove =
                  permissions.canManageGrants &&
                  (member.role !== 'manager' || permissions.canTransfer || isSelf);
                const roleOptions = [
                  ...(permissions.canTransfer
                    ? [{ label: t('marketplace.publishers.manage.team.members.roleManager'), value: 'manager' }]
                    : []),
                  { label: t('marketplace.publishers.manage.team.members.roleCreator'), value: 'creator' },
                ];

                return (
                  <li key={member.id} className="publisher-team-section__member-row">
                    {/* Avatar — sphere identity or member avatar (no name label) */}
                    {member.subjectSphereId ? (
                      <SphereIdentityAtom
                        sphereId={member.subjectSphereId}
                        glyph={member.glyph ?? undefined}
                        glyphColor={member.glyphColor ?? undefined}
                        imageId={member.imageId ?? undefined}
                        size="medium"
                        className="publisher-team-section__sphere-identity"
                      />
                    ) : (
                      <MemberAvatar name={member.displayName} />
                    )}

                    {/* Member info: 2-line layout
                        Line 1: Name + You badge
                        Line 2: Email · Join date */}
                    <div className="publisher-team-section__member-info">
                      <div className="publisher-team-section__member-line-1">
                        <span className="publisher-team-section__member-name">{displayName}</span>
                        {isSelf && (
                          <span className="publisher-team-section__you-badge">
                            {t('marketplace.publishers.manage.team.members.youBadge')}
                          </span>
                        )}
                      </div>
                      <div className="publisher-team-section__member-line-2">
                        {member.email && (
                          <>
                            <span className="publisher-team-section__member-email">{member.email}</span>
                            <span className="publisher-team-section__meta-sep">·</span>
                          </>
                        )}
                        <span className="publisher-team-section__member-joined">{joinedStr}</span>
                      </div>
                    </div>

                    {/* Role controls — owner badge or role dropdown + remove button */}
                    <div className="publisher-team-section__role-controls">
                      {isOwner && (
                        <span className="publisher-team-section__owner-badge">
                          <i className="pi pi-crown" aria-hidden="true" />
                          {t('marketplace.publishers.manage.team.members.ownerLabel')}
                        </span>
                      )}
                      {!isOwner && canEditRole && (
                        <DropdownAtom
                          className="publisher-team-section__role-select"
                          dropdownSize="small"
                          value={member.role}
                          options={roleOptions}
                          onChange={(e) =>
                            handleRoleChange(member, e.value as 'manager' | 'creator')
                          }
                          disabled={isMutating}
                          ariaLabel={t('marketplace.publishers.manage.team.invite.roleLabel')}
                        />
                      )}
                      {!isOwner && !canEditRole && (
                        <span className="publisher-team-section__role-label-readonly">
                          {roleLabel(member.role)}
                        </span>
                      )}

                      {!isOwner && canRemove && (
                        <button
                          type="button"
                          className="publisher-team-section__remove-btn"
                          onClick={() => handleRemoveClick(member)}
                          disabled={isMutating}
                          aria-label={`${t('marketplace.publishers.manage.team.members.removeButton')} ${displayName}`}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Add teammate card — only for users who can manage grants.
          --overflow lets the typeahead results overlay escape the card
          (the shared card clips with overflow:hidden). */}
      {permissions.canManageGrants && (
        <section className="publisher-team-section__card publisher-team-section__card--overflow">
          <header className="publisher-team-section__card-header">
            <h2 className="publisher-team-section__card-title">
              {t('marketplace.publishers.manage.team.invite.title')}
            </h2>
            <p className="publisher-team-section__card-subtitle">
              {t('marketplace.publishers.manage.team.invite.subtitle')}
            </p>
          </header>
          <div className="publisher-team-section__card-body">
            <AddTeammateMolecule
              members={members}
              canTransfer={permissions.canTransfer}
              onAdd={handleAdd}
              isMutating={isMutating}
            />
          </div>
        </section>
      )}

      {/* Roles explainer card */}
      <section className="publisher-team-section__card publisher-team-section__card--roles">
        <header className="publisher-team-section__card-header">
          <h2 className="publisher-team-section__card-title">
            {t('marketplace.publishers.manage.team.roles.title')}
          </h2>
          <p className="publisher-team-section__card-subtitle">
            {t('marketplace.publishers.manage.team.roles.subtitle')}
          </p>
        </header>
        <div className="publisher-team-section__card-body">
          <RoleExplainerSection />
        </div>
      </section>
    </div>
  );
};

export default PublisherTeamSectionOrganism;
